import { useQuery } from '@tanstack/react-query';

import { tourResolveQueryOptions } from './resolve-query';
import { useTour } from './tour-context';
import type { TourDefinition, TourResolver } from './types';

export type TourBlocker = 'needs-store' | 'needs-record' | null;

const NO_RECORD: TourResolver = { key: 'none', thing: '', load: () => Promise.resolve({}) };

/**
 * Whether a tour can start now: store tours need a store, record tours a record. Record tours
 * look one up with the same cache entry `start` uses, so starting afterwards costs no request.
 */
export function useTourReadiness(definition: TourDefinition): {
  blocker: TourBlocker;
  isPending: boolean;
} {
  const tour = useTour();
  const resolver = definition.resolve;
  const needsStore = definition.route.includes('$storeId') && tour.storeId === undefined;
  const record = useQuery({
    ...tourResolveQueryOptions(resolver ?? NO_RECORD, tour.storeId),
    enabled: resolver !== undefined && !needsStore,
  });
  if (needsStore) return { blocker: 'needs-store', isPending: false };
  if (!resolver) return { blocker: null, isPending: false };
  if (record.isPending) return { blocker: null, isPending: true };
  return { blocker: record.data ? null : 'needs-record', isPending: false };
}
